import { m, AnimatePresence } from 'framer-motion';
import { Lock, X, ShieldCheck } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * KioskSettings - Administrative controls for Kiosk Mode.
 * Normalized to use industrial standard UI components.
 */
const KioskSettings = ({ 
    user, 
    showUnlockModal, 
    onCloseUnlockModal, 
    unlockPassword, 
    setUnlockPassword, 
    onConfirmUnlock 
}) => {
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        if (showUnlockModal) {
            // Small delay to allow animation to start
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showUnlockModal]);

    // Only administrators can see these controls
    if (user?.role !== 'ADMIN') return null;

    const handleConfirmUnlock = (e) => {
        e.preventDefault();
        onConfirmUnlock();
    };

    return (
        <AnimatePresence>
            {showUnlockModal && (
                <m.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[1000] flex items-center justify-center bg-primary/90 backdrop-blur-xl p-6"
                >
                    <div className="w-full max-w-lg bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col border border-white/20">
                        {/* Header */}
                        <div className="bg-slate-50 border-b border-slate-100 p-8 flex items-center justify-between">
                            <div className="flex flex-col">
                                <h2 className="text-2xl font-black text-primary uppercase tracking-tighter italic">Admin Authorization</h2>
                                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-1">Provide credentials to deactivate lockdown mode</p>
                            </div>
                            <button 
                                onClick={onCloseUnlockModal}
                                className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-500/20 transition-all active:scale-90 shadow-sm"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-8 flex flex-col gap-8">
                            {/* Admin Context */}
                            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-white">
                                    <ShieldCheck size={24} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 italic">Active Admin</span>
                                    <span className="text-sm font-black text-primary uppercase">{user?.displayName || user?.email}</span>
                                </div>
                            </div>

                            <form onSubmit={handleConfirmUnlock} className="flex flex-col gap-3">
                                <label htmlFor="unlock-access-key" className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-2 italic">Access Key</label>
                                <input
                                    ref={inputRef}
                                    id="unlock-access-key"
                                    type="password"
                                    autoFocus
                                    value={unlockPassword}
                                    onChange={(e) => setUnlockPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full h-16 px-6 bg-slate-50 border-2 border-slate-100 rounded-2xl text-center text-2xl font-black text-primary outline-none focus:border-accent focus:bg-white transition-all tracking-widest"
                                />
                            </form>
                        </div>

                        {/* Footer Actions */}
                        <div className="p-8 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
                            <button
                                onClick={onConfirmUnlock}
                                className="w-full h-14 bg-primary hover:bg-primary/90 text-white rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all active:scale-95 shadow-xl shadow-primary/20"
                            >
                                <Lock size={18} />
                                <span>Unlock Terminal</span>
                            </button>
                            
                            <p className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-tighter italic">
                                All unlock attempts are registered in the security manifest
                            </p>
                        </div>
                    </div>
                </m.div>
            )}
        </AnimatePresence>
    );
};

KioskSettings.propTypes = {
    user: PropTypes.shape({
        displayName: PropTypes.string,
        email: PropTypes.string,
        role: PropTypes.string
    }),
    showUnlockModal: PropTypes.bool.isRequired,
    onCloseUnlockModal: PropTypes.func.isRequired,
    unlockPassword: PropTypes.string.isRequired,
    setUnlockPassword: PropTypes.func.isRequired,
    onConfirmUnlock: PropTypes.func.isRequired
};

export default React.memo(KioskSettings);
